import {
  IChartApi,
  MismatchDirection,
  MouseEventParams,
  Time
} from 'lightweight-charts';
import { useEffect, useRef } from 'react';
import { useChartContext } from '../components/chart.context';
import {
  getCrosshairPricePosition$,
  getCrosshairPriceSyncInstanceId,
  updateCrosshairPricePosition
} from '../services/global-crosshair-price-sync.service';
import {
  getCrosshairPosition$,
  getCrosshairSyncInstanceId,
  updateCrosshairPosition as updateGlobalCrosshair
} from '../services/global-crosshair-sync.service';
import {
  adjustTimeForTimeframe,
  ChartTime,
  needsTimeAdjustment,
  resolveXPos,
  safeTimeToCoordinate,
  toSecondsIfMs
} from '../services/timeframe-utils';
import { toNumericTimeOrNull } from './utils/time-normalization.utils';
import {
  attachCrosshairPriceLineElements,
  createCrosshairPriceLineElements,
  CrosshairPriceLineElements,
  removeCrosshairPriceLineElements,
  updateCrosshairPriceLine
} from './utils/crosshair-price-line.utils';
import {
  CrosshairElements,
  updateCrosshairPosition
} from './utils/crosshair.utils';

/**
 * Shows a synced *horizontal* (price) crosshair line on other timeframe charts.
 *
 * Notes:
 * - `useChartSynchronization` already handles the *vertical* line + global time broadcast.
 * - This hook intentionally doesn't modify `useChartSynchronization` nor the scroll sync hook.
 */
export const useCrosshairPriceLineSynchronization = (
  timeframeIndex?: number,
  timeframe?: string
) => {
  const {
    chartRef,
    candlestickSeriesRef,
    indicatorChartRefs,
    chartInitialized
  } = useChartContext();
  const elementsRef = useRef<CrosshairPriceLineElements | null>(null);

  const resolveTimeElements = (chart: IChartApi): CrosshairElements | null => {
    const root = chart.chartElement();
    const line = root.querySelector(
      '[data-crosshair-sync="time-line"]'
    ) as HTMLDivElement | null;
    const label = root.querySelector(
      '[data-crosshair-sync="time-label"]'
    ) as HTMLDivElement | null;

    if (!line || !label) return null;
    return { line, label };
  };

  const collectIndicatorCharts = () =>
    Object.values(indicatorChartRefs.current).flat();

  const updateTimeLineOnChart = (
    chart: IChartApi,
    time: ChartTime,
    targetTimeframe?: string
  ) => {
    const elements = resolveTimeElements(chart);
    if (!elements) return;

    if (time == null) {
      updateCrosshairPosition(elements, null);
      return;
    }

    const { x, usedTime } =
      typeof time === 'number'
        ? resolveXPos(chart, time, targetTimeframe)
        : { x: safeTimeToCoordinate(chart, time) };

    const numericTime = toNumericTimeOrNull((usedTime ?? time) as Time | null);
    updateCrosshairPosition(elements, x, numericTime ?? undefined);
  };

  const resolveMainPriceAtTime = (time: number | null): number | null => {
    if (time == null) return null;
    const mainChart = chartRef.current;
    const series = candlestickSeriesRef.current;
    if (!mainChart || !series) return null;

    const index = mainChart.timeScale().timeToIndex(time as Time, true);
    if (index == null) return null;

    const data = series.dataByIndex(
      index as number,
      MismatchDirection.NearestLeft
    ) as any;

    if (!data) return null;

    const raw =
      typeof data.close === 'number'
        ? data.close
        : typeof data.value === 'number'
          ? data.value
          : null;

    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  };

  // Attach / detach DOM elements
  useEffect(() => {
    if (!chartInitialized || !chartRef.current) return;

    const chart = chartRef.current;
    const elements = elementsRef.current ?? createCrosshairPriceLineElements();

    elementsRef.current = elements;
    attachCrosshairPriceLineElements(chart, elements);

    return () => {
      removeCrosshairPriceLineElements(chart, elements);
    };
  }, [chartInitialized]);

  // Emit current price level from the hovered (source) chart
  useEffect(() => {
    if (!chartInitialized || !chartRef.current) return;

    const chart = chartRef.current;

    let rafId: number | null = null;
    let pending: { time: number | null; price: number | null } | null = null;

    const flush = () => {
      rafId = null;
      if (!pending) return;
      const next = pending;
      pending = null;

      updateCrosshairPricePosition({
        ...next,
        sourceTimeframeIndex: timeframeIndex,
        sourceTimeframe: timeframe
      });
    };

    const schedule = (next: { time: number | null; price: number | null }) => {
      pending = next;
      if (rafId !== null) return;
      rafId = requestAnimationFrame(flush);
    };

    const handleCrosshairMove = (param: MouseEventParams<Time>) => {
      const time = toNumericTimeOrNull(param?.time ?? null);
      const pointY = param?.point?.y;
      const series = candlestickSeriesRef.current;

      const priceRaw =
        series && typeof pointY === 'number'
          ? (series.coordinateToPrice(pointY) as number | null)
          : null;

      const price =
        typeof priceRaw === 'number' && Number.isFinite(priceRaw)
          ? priceRaw
          : null;

      const indicatorCharts = collectIndicatorCharts();
      const numericTime = time ?? null;

      indicatorCharts.forEach((indicatorChart) => {
        updateTimeLineOnChart(indicatorChart, numericTime, timeframe);
      });

      if (series) {
        let activeElements = elementsRef.current;
        if (!activeElements) {
          activeElements = createCrosshairPriceLineElements();
          elementsRef.current = activeElements;
          attachCrosshairPriceLineElements(chart, activeElements);
        } else if (!activeElements.line.isConnected) {
          attachCrosshairPriceLineElements(chart, activeElements);
        }

        if (price === null || time === null) {
          updateCrosshairPriceLine(activeElements, null);
        } else {
          const y = series.priceToCoordinate(price);
          if (y === null) {
            updateCrosshairPriceLine(activeElements, null);
          } else {
            const labelText = series.priceFormatter().format(price);
            updateCrosshairPriceLine(activeElements, y, labelText);
          }
        }
      }

      schedule({ time, price });
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [chartInitialized, timeframeIndex, timeframe]);

  // Emit time from indicator charts so main chart syncs when hovering indicators
  useEffect(() => {
    if (!chartInitialized) return;

    const subscriptionsRef = new Map<IChartApi, () => void>();

    const ensureSubscriptions = () => {
      const indicatorCharts = collectIndicatorCharts();
      if (!indicatorCharts.length) return;

      indicatorCharts.forEach((indicatorChart) => {
        if (subscriptionsRef.has(indicatorChart)) return;

        const handleCrosshairMove = (param: MouseEventParams<Time>) => {
          const time = toNumericTimeOrNull(param?.time ?? null);

          if (time == null) {
            const mainChart = chartRef.current;
            if (mainChart) updateTimeLineOnChart(mainChart, null, timeframe);

            const elements = elementsRef.current;
            if (elements) updateCrosshairPriceLine(elements, null);

            updateGlobalCrosshair({
              time: null,
              sourceTimeframeIndex: timeframeIndex,
              sourceTimeframe: timeframe
            });
            updateCrosshairPricePosition({
              time: null,
              price: null,
              sourceTimeframeIndex: timeframeIndex,
              sourceTimeframe: timeframe
            });
            return;
          }

          const mainChart = chartRef.current;
          if (mainChart) updateTimeLineOnChart(mainChart, time, timeframe);

          const price = resolveMainPriceAtTime(time);
          const series = candlestickSeriesRef.current;
          const elements = elementsRef.current;

          if (series && elements && price != null) {
            const y = series.priceToCoordinate(price);
            if (y != null) {
              const labelText = series.priceFormatter().format(price);
              updateCrosshairPriceLine(elements, y, labelText);
            } else {
              updateCrosshairPriceLine(elements, null);
            }
          } else if (elements) {
            updateCrosshairPriceLine(elements, null);
          }

          updateGlobalCrosshair({
            time,
            sourceTimeframeIndex: timeframeIndex,
            sourceTimeframe: timeframe
          });
          updateCrosshairPricePosition({
            time,
            price,
            sourceTimeframeIndex: timeframeIndex,
            sourceTimeframe: timeframe
          });
        };

        indicatorChart.subscribeCrosshairMove(handleCrosshairMove);
        subscriptionsRef.set(indicatorChart, () =>
          indicatorChart.unsubscribeCrosshairMove(handleCrosshairMove)
        );
      });

      subscriptionsRef.forEach((unsubscribe, chart) => {
        if (indicatorCharts.includes(chart)) return;
        unsubscribe();
        subscriptionsRef.delete(chart);
      });
    };

    ensureSubscriptions();
    const retryTimeouts = [50, 150, 300];
    const timers = retryTimeouts.map((ms) =>
      setTimeout(() => ensureSubscriptions(), ms)
    );

    return () => {
      timers.forEach(clearTimeout);
      subscriptionsRef.forEach((unsubscribe) => unsubscribe());
      subscriptionsRef.clear();
    };
  }, [chartInitialized, timeframeIndex, timeframe]);

  // Display vertical crosshair line (time) across timeframes
  useEffect(() => {
    if (!chartInitialized || !chartRef.current) return;

    const subscription = getCrosshairPosition$().subscribe((data) => {
      const isSameInstance =
        data.sourceInstanceId === getCrosshairSyncInstanceId();

      const isOwnTimeframeOnThisInstance =
        isSameInstance && data.sourceTimeframeIndex === timeframeIndex;

      if (isOwnTimeframeOnThisInstance) return;

      if (data.time == null) {
        const mainChart = chartRef.current;
        if (mainChart) updateTimeLineOnChart(mainChart, null, timeframe);

        collectIndicatorCharts().forEach((indicatorChart) => {
          updateTimeLineOnChart(indicatorChart, null, timeframe);
        });
        return;
      }

      let t: ChartTime = data.time;

      if (
        typeof t === 'number' &&
        data.sourceTimeframe &&
        timeframe &&
        needsTimeAdjustment(data.sourceTimeframe, timeframe)
      ) {
        t = adjustTimeForTimeframe(toSecondsIfMs(t) as number, timeframe);
      } else {
        t = toSecondsIfMs(t);
      }

      const mainChart = chartRef.current;
      if (mainChart) updateTimeLineOnChart(mainChart, t, timeframe);

      collectIndicatorCharts().forEach((indicatorChart) => {
        updateTimeLineOnChart(indicatorChart, t, timeframe);
      });
    });

    return () => subscription.unsubscribe();
  }, [chartInitialized, chartRef, timeframeIndex, timeframe]);

  // Display the horizontal line when another timeframe emits a price level
  useEffect(() => {
    if (!chartInitialized || !chartRef.current) return;

    // Ensure elements exist and are attached (covers race where attach effect hasn't run yet)
    if (!elementsRef.current) {
      const elements = createCrosshairPriceLineElements();
      elementsRef.current = elements;
      attachCrosshairPriceLineElements(chartRef.current, elements);
    }

    const elements = elementsRef.current!;

    const subscription = getCrosshairPricePosition$().subscribe((data) => {
      const isSameInstance =
        data.sourceInstanceId === getCrosshairPriceSyncInstanceId();

      const isOwnTimeframeOnThisInstance =
        isSameInstance && data.sourceTimeframeIndex === timeframeIndex;

      if (isOwnTimeframeOnThisInstance) {
        updateCrosshairPriceLine(elements, null);
        return;
      }

      if (data.time == null || data.price == null) {
        updateCrosshairPriceLine(elements, null);
        return;
      }

      const series = candlestickSeriesRef.current;
      if (!series) return;

      const y = series.priceToCoordinate(data.price);
      if (y === null) {
        updateCrosshairPriceLine(elements, null);
        return;
      }

      const labelText = series.priceFormatter().format(data.price);
      updateCrosshairPriceLine(elements, y, labelText);
    });

    return () => subscription.unsubscribe();
  }, [chartInitialized, chartRef, timeframeIndex]);
};
